<template>
  <div>
    <navbar :links="links" />
    <v-layout class="mt-4 mb-2">
      <span class="grey--text text--darken-2 subheading">{{
        $t('editSpotCategoriesHint')
      }}</span>
    </v-layout>
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <!-- Categories List -->
    <v-layout
      v-if="!loading && editableCategories.length === 0"
      class="mt-3"
      column
      justify-center
      align-center
    >
      <div
        class="grey--text subheading"
        data-cy="no-own-categories-subheader"
      >
        {{ $t('noCustomSpotCategoriesYet') }}
      </div>
    </v-layout>
    <v-list v-if="!loading">
      <template v-for="(category, index) in editableCategories">
        <v-list-tile
          :key="category.id"
          class="list-tile-min-height list-tile-auto-height"
        >
          <v-list-tile-content>
            <v-list-tile-title class="auto-height wrap">
              <v-layout
                align-center
                :wrap="$vuetify.breakpoint.xsOnly && currentlyEditing"
              >
                <v-flex xs8>
                  <div
                    v-if="!isEditingCategory(category)"
                    class="clickable"
                    @dblclick="startEditCategory(category)"
                  >
                    <span
                      :class="{'grey--text': currentlyEditing}"
                      :title="$t('name')"
                    >{{ category.name }}</span>
                  </div>
                  <!-- Edit Category Name and Group -->
                  <div
                    v-else
                    class="mt-3"
                    :class="{'ml-3': $vuetify.breakpoint.smAndUp}"
                  >
                    <v-text-field
                      ref="textfields"
                      v-model="editedCategoryName"
                      :label="$t('name')"
                      :rules="[rules.min, rules.max, rules.uniqueEdited]"
                      class="show-messages"
                      data-cy="edit-category-name-textfield"
                      @keyup.esc="abortEditCategory()"
                      @keyup.enter="editFormIsValid ? updateCategory() : null"
                    />
                    <v-select
                      v-if="hasMoreThanOneUploadGroup"
                      v-model="editedCategoryGroupId"
                      item-text="name"
                      item-value="id"
                      persistent-hint
                      data-cy="edit-category-group-select"
                      class="mt-1 mb-3 edit-category-group-select"
                      :items="$store.getters.uploadgroups"
                      :label="$t('group')"
                      :hint="$t('chooseGroupForCategory')"
                    />
                  </div>
                </v-flex>
                <v-flex xs2>
                  <v-layout
                    v-if="!isEditingCategory(category)"
                    wrap
                    class="mb-1"
                  >
                    <v-layout
                      v-visible="category.num_contents.radio"
                      align-center
                      class="mr-1"
                      style="width: 50px"
                    >
                      <v-icon
                        small
                        class="mr-2"
                        :title="$t('numContentsWithCategory')"
                      >
                        music_note
                      </v-icon>
                      <span
                        class="grey--text"
                        :title="$t('numContentsWithCategory')"
                      >{{ category.num_contents.radio }}</span>
                    </v-layout>
                    <v-layout
                      v-visible="category.num_contents.tv"
                      align-center
                      style="width: 50px"
                    >
                      <v-icon
                        small
                        class="mr-2"
                        :title="$t('numContentsWithCategory')"
                      >
                        tv
                      </v-icon>
                      <span
                        class="grey--text"
                        :title="$t('numContentsWithCategory')"
                      >{{ category.num_contents.tv }}</span>
                    </v-layout>
                  </v-layout>
                </v-flex>
                <v-flex class="mr-1 mb-2">
                  <v-layout align-center>
                    <!-- Go to filtered Contents List Button -->
                    <v-btn
                      v-if="!isEditingCategory(category)"
                      v-visible="
                        category.num_contents.radio || category.num_contents.tv
                      "
                      class="mx-1 white msr--text"
                      icon
                      :title="$t('goToContentsListFilteredByCategory')"
                      @click="goToFilteredContentsList(category)"
                    >
                      <v-icon>filter_list</v-icon>
                    </v-btn>
                    <!-- Edit Button VS Confirm/Abort Buttons -->
                    <v-btn
                      v-if="!isEditingCategory(category)"
                      class="mx-1 white msr--text"
                      icon
                      data-cy="edit-category-start-btn"
                      :disabled="currentlyEditing"
                      @click="startEditCategory(category)"
                    >
                      <v-icon>edit</v-icon>
                    </v-btn>
                    <v-layout
                      v-else
                      align-center
                    >
                      <btn-abort
                        round
                        @click="abortEditCategory()"
                      />
                      <btn-ok
                        data-cy="edit-category-confirm-btn"
                        :disabled="!editFormIsValid"
                        :loading="busy"
                        round
                        @click="updateCategory()"
                      />
                    </v-layout>
                    <!-- Delete Button VS Confirm Button-->
                    <v-btn
                      v-if="!isEditingCategory(category)"
                      flat
                      class="ml-1 mr-0 white msr--text"
                      icon
                      data-cy="delete-category-btn"
                      :disabled="currentlyEditing"
                      @click="onDeletionButtonClicked(category)"
                    >
                      <v-icon>delete</v-icon>
                    </v-btn>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-list-tile-title>
            <v-list-tile-sub-title
              v-if="hasMoreThanOneUploadGroup && !isEditingCategory(category)"
              :title="$t('group')"
              class="mb-1"
              data-cy="category-group-subtitle"
            >
              {{ category.group.name }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-divider
          v-if="index < editableCategories.length - 1"
          :key="`divider_${index}`"
        />
      </template>
    </v-list>
    <!-- Create new Category FAB -->
    <v-layout justify-end>
      <v-tooltip left>
        <v-btn
          v-if="!loading"
          slot="activator"
          dark
          fab
          fixed
          :bottom="fixCreationButtonToBottom"
          right
          class="msr"
          data-cy="new-category-create-btn"
          :class="{
            'desktop-mode': $store.state.common.desktopMode,
            safari: $store.state.common.runtimeEnv.safari,
          }"
          @click="startCreateCategory()"
        >
          <v-icon medium>
            add
          </v-icon>
        </v-btn>
        <span>{{ $t('createNewCategory') }}</span>
      </v-tooltip>
    </v-layout>
    <v-layout
      v-if="!loading"
      class="mt-2 subheading grey--text"
    >
      <div>
        {{ $t('plusDefaultCategories') }}:
        {{ defaultCategories.map((c) => c.name).join(', ') }}
      </div>
    </v-layout>
    <!-- Additional Scrolling Space -->
    <div style="min-height: 60px" />
    <!-- Create Category Dialog -->
    <v-dialog
      v-model="showCreateDialog"
      :width="
        hasMoreThanOneUploadGroup
          ? $store.state.common.largeDialogWidth
          : 'auto'
      "
    >
      <v-card>
        <v-card-title>
          <h1 class="headline">
            {{ $t('createNewCategory') }}
          </h1>
          <v-btn
            class="ml-auto mr-0 my-0"
            flat
            icon
            :title="$t('cancel')"
            @click="abortCreateCategory()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <!-- New Name -->
          <v-text-field
            ref="createnewtextfield"
            :key="showCreateDialog"
            v-model="newCategoryName"
            counter="30"
            class="pb-1"
            data-cy="new-category-name-textfield"
            :label="$t('name')"
            :rules="[rules.min, rules.max, rules.unique]"
            @keyup.esc="abortCreateCategory()"
            @keyup.enter="createFormIsValid ? createNewCategory() : null"
          />
          <!-- Select Upload Group if there are multiple -->
          <v-select
            v-if="hasMoreThanOneUploadGroup"
            v-model="selectedUploadgroupId"
            item-text="name"
            item-value="id"
            persistent-hint
            class="cy-new-category-group-select"
            :items="$store.getters.uploadgroups"
            :label="$t('group')"
            :hint="$t('chooseGroupForCategory')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            data-cy="new-category-confirm-btn"
            :disabled="!createFormIsValid"
            :loading="busy"
            round
            @click="createNewCategory()"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Deletion Confirmation Dialog -->
    <v-dialog
      v-model="showConfirmDeletionDialog"
      :width="$store.state.common.largeDialogWidth"
    >
      <v-card>
        <v-card-title class="headline pb-0">
          {{ $t('deleteCategory') }}:
          <b v-if="categoryToDelete">&nbsp;{{ categoryToDelete.name }}</b>
          <v-btn
            class="ml-auto mr-0 my-0"
            flat
            icon
            :title="$t('cancel')"
            @click="resetConfirmDeletionDialog()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          {{ $t('askDeleteUserSpotCategory_1') }}
          <span v-if="!deleteCategoryContentsAlso">{{
            $t('askDeleteUserSpotCategory_1b')
          }}</span>.
          <v-checkbox
            v-if="allowDeletingContentsWithCategory && numContentsToDeleteLabel"
            v-model="deleteCategoryContentsAlso"
            color="msr"
            hide-details
            :class="{'msr--text': deleteCategoryContentsAlso}"
            :label="numContentsToDeleteLabel"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            round
            :loading="busy"
            data-cy="confirm-delete-category-btn"
            @click="
              deleteCategory(categoryToDelete, deleteCategoryContentsAlso)
            "
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import BtnAbort from '@/components/ButtonAbort';
import BtnOk from '@/components/ButtonOk';
import Navbar from '@/components/Navbar';

export default {
  name: 'Categories',
  components: {
    BtnAbort,
    BtnOk,
    Navbar,
  },
  data() {
    const vm = this;
    return {
      links: ['dashboard', 'contents', 'categories'],
      loading: false,
      busy: false,
      maxNumberOfDeleteableContents: 50, // See settings.MAX_CONTENTS_DELETEABLE_WITH_CATEGORY in backend.
      rules: {
        min: (v) => (!!v && v.trim().length > 0) || vm.$t('tooShort'),
        max: (v) => !v || v.length <= 30 || vm.$t('tooLong'),
        unique: (v) =>
          vm.isNewCategoryNameUnique(v) || vm.$t('nameAlreadyInUse'),
        uniqueEdited: (v) => {
          const otherCategories = vm.categories.filter(
            (c) => c.id !== vm.categoryToEdit.id
          );
          return (
            vm.isNewCategoryNameUnique(v, otherCategories) ||
            vm.$t('nameAlreadyInUse')
          );
        },
      },

      showCreateDialog: false,
      newCategoryName: '',

      /** Do not use directly, instead use getSelectedUploadgroupId(). */
      selectedUploadgroupId: null,

      showConfirmDeletionDialog: false,
      categoryToDelete: null,
      deleteCategoryContentsAlso: false,

      categoryToEdit: {},
      editedCategoryName: '',
      editedCategoryGroupId: null,
    };
  },
  computed: {
    allowDeletingContentsWithCategory() {
      if (!this.categoryToDelete) {
        return '';
      }
      return (
        this.categoryToDelete.num_contents.radio +
          this.categoryToDelete.num_contents.tv <=
        this.maxNumberOfDeleteableContents
      );
    },
    numContentsToDeleteLabel() {
      if (!this.categoryToDelete) {
        return '';
      }
      const numContents =
        this.categoryToDelete.num_contents.radio +
        this.categoryToDelete.num_contents.tv;
      if (!numContents) {
        return '';
      }
      return this.$t('askDeleteUserSpotCategory_3').replace(
        '{{ num }}',
        numContents
      );
    },
    fixCreationButtonToBottom() {
      return (
        this.$vuetify.breakpoint.xsOnly || this.editableCategories.length >= 6
      );
    },
    currentlyEditing() {
      return !!this.categoryToEdit.id;
    },
    hasMoreThanOneUploadGroup() {
      return this.$store.getters.uploadgroups.length > 1;
    },
    uploadgroupIds() {
      return this.$store.getters.uploadgroups.map((group) => group.id);
    },
    categories() {
      return this.$store.state.common.categories;
    },
    /**
     * Categories are editable by the User when they are non-default
     * and the User has content-management rights for their Group.
     */
    editableCategories() {
      const vm = this;

      function isContentManagerForGroup(group) {
        return vm.uploadgroupIds.indexOf(group.id) !== -1;
      }

      const nonDefaults = this.categories.filter((c) => !c.is_default);
      const editableByUser = nonDefaults.filter((c) => {
        return c.group && isContentManagerForGroup(c.group);
      });
      return editableByUser;
    },
    defaultCategories() {
      return this.categories.filter((c) => c.is_default);
    },
    editFormIsValid() {
      const hasGroup = this.editedCategoryGroupId !== null;

      const otherCategories = this.categories.filter(
        (c) => c.id !== this.categoryToEdit.id
      );
      const hasUniqueName = this.isNewCategoryNameUnique(
        this.editedCategoryName,
        otherCategories
      );
      const hasValidName =
        this.editedCategoryName.trim().length > 0 &&
        this.editedCategoryName.length <= 30;

      return (
        this.categoryToEdit.id && hasGroup && hasUniqueName && hasValidName
      );
    },
    createFormIsValid() {
      const isUnique = this.isNewCategoryNameUnique(this.newCategoryName);
      const nameOk =
        isUnique &&
        this.newCategoryName.trim().length > 0 &&
        this.newCategoryName.length <= 30;
      const groupOk = !!this.getSelectedUploadgroupId();
      return nameOk && groupOk;
    },
  },
  mounted() {
    this.fetchInitialData();
  },
  methods: {
    isNewCategoryNameUnique(newName, categories = null) {
      if (categories === null) {
        categories = this.categories;
      }
      const categoryNames = categories.map((category) => category.name);
      const isUnique = categoryNames.indexOf(newName) === -1;
      return isUnique;
    },
    isBeingUsedInContents(category) {
      return (
        category.num_contents &&
        (category.num_contents.radio || category.num_contents.tv)
      );
    },
    fetchCategories() {
      return this.$store.dispatch('LIST_CATEGORIES');
    },
    fetchInitialData() {
      this.loading = true;
      return Promise.all([this.fetchCategories()]).finally(() => {
        this.loading = false;
      });
    },
    isEditingCategory(category) {
      return this.categoryToEdit.id == category.id;
    },
    startEditCategory(category) {
      this.categoryToEdit = category;
      this.editedCategoryName = category.name;
      this.editedCategoryGroupId = category.group.id;

      this.$nextTick(() => {
        const textfield = this.$refs.textfields[0];
        if (textfield) {
          textfield.focus();
        }
      });
    },
    abortEditCategory() {
      this.categoryToEdit = {};
      this.editedCategoryName = '';
      this.editedCategoryGroupId = null;
      this.busy = false;
    },
    /**
     * Use first Group if there is only one, User-picked Group otherwise.
     */
    getSelectedUploadgroupId() {
      let groupId = this.uploadgroupIds[0];
      if (this.hasMoreThanOneUploadGroup) {
        groupId = this.selectedUploadgroupId;
      }
      return groupId;
    },
    startCreateCategory() {
      this.newCategoryName = '';
      this.showCreateDialog = true;

      this.$nextTick(() => {
        const textfield = this.$refs.createnewtextfield;
        if (textfield) {
          textfield.focus();
        }
      });
    },
    abortCreateCategory() {
      this.newCategoryName = '';
      this.selectedUploadgroupId = null;
      this.busy = false;
      this.showCreateDialog = false;
    },
    updateCategory() {
      const noChanges =
        this.categoryToEdit.name === this.editedCategoryName &&
        this.categoryToEdit.group.id === this.editedCategoryGroupId;
      if (noChanges) {
        this.abortEditCategory();
        return;
      }
      this.busy = true;
      this.$api
        .updateUserSpotCategory(
          this.categoryToEdit.id,
          this.editedCategoryName,
          this.editedCategoryGroupId
        )
        .then(() => {
          this.abortEditCategory();
          this.fetchCategories();
        })
        .catch(() => {
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onDeletionButtonClicked(category) {
      this.categoryToDelete = category;
      this.showConfirmDeletionDialog = true;
    },
    resetConfirmDeletionDialog() {
      this.categoryToDelete = null;
      this.deleteCategoryContentsAlso = false;
      this.showConfirmDeletionDialog = false;
    },
    deleteCategory(category, contentsAlso) {
      this.busy = true;
      this.$api
        .deleteUserSpotCategory(category.id, contentsAlso)
        .then(() => {
          this.fetchCategories();
        })
        .catch(() => {
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
          this.showConfirmDeletionDialog = false;
        });
    },
    createNewCategory() {
      const groupId = this.getSelectedUploadgroupId();

      this.busy = true;
      this.$api
        .createUserSpotCategory(this.newCategoryName, groupId)
        .then(() => {
          this.abortCreateCategory();
          this.fetchCategories();
        })
        .catch(() => {
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    /**
     * Reset contents list, filter by given category and go to contents page.
     */
    goToFilteredContentsList(category) {
      this.$store.commit('RESET_CONTENT_FILTERS');
      this.$store.commit('SET_CONTENTS_QUERY_SELECTED_CATEGORY_IDS', [
        category.id,
      ]);
      this.$router.push('/content');
    },
  },
};
</script>

<style scoped>
.msr--text >>> .v-label {
  color: var(--msr-color-primary);
}

.hoverable >>> .v-list__tile:hover {
  background: rgba(0, 0, 0, 0.04);
}

/** Default for v-text-field inside a v-list seems to hide messages. */
.show-messages >>> .v-text-field__details .v-messages {
  display: initial;
  padding: 8px 0 4px 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/SpotCategories.vue